// VisionShell — shell de cristal (reemplaza sidebar+header). Environment + TopBar
// cápsula + ventana de contenido + VisionLauncher (el menú). Theming por rol.
import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LayoutGrid, LogOut, Stethoscope } from "lucide-react";

import { LocaleSwitcher } from "@/components/locale-switcher";
import { CATALOG, type LauncherItem } from "@/components/vision/catalog";
import { VisionEnvironment } from "@/components/vision/vision-environment";
import { VisionLauncher } from "@/components/vision/vision-launcher";
import { ThemeToggle } from "@/components/vision/theme-toggle";
import { WallpaperPicker } from "@/components/vision/wallpaper-picker";
import { openTriageModule } from "@/lib/triage-bridge";
import { useAuthStore } from "@/lib/auth-store";
import { themeFor } from "@/lib/role-theme";
import { useWallpaper } from "@/lib/wallpapers";
import { cn } from "@/lib/utils";

function initials(name?: string) {
  if (!name) return "··";
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export function VisionShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const logout = useAuthStore((s) => s.logout);
  const [launcherOpen, setLauncherOpen] = useState(false);

  // Gate de montaje: en la carga inicial, el trabajo síncrono de boot (parseo del
  // bundle, mount, fuentes) cancelaba las animaciones de entrada de Framer de la
  // página y dejaba el contenido invisible. Montamos el contenido en el siguiente
  // frame, cuando el hilo principal está libre, para que Framer anime limpio.
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setPageReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // ⌘K / Ctrl+K abre/cierra el launcher.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setLauncherOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // theme + wallpaper ANTES del early return (regla de hooks: useWallpaper no
  // puede ir después de un return condicional).
  const theme = themeFor(user);
  const [wallpaperKey] = useWallpaper(theme.wallpaper);

  if (!accessToken) return <Navigate to="/login" replace />;

  const isTeacher =
    user?.role === "teacher" ||
    user?.role === "institution_admin" ||
    user?.role === "platform_admin";
  const isAdmin =
    user?.role === "institution_admin" || user?.role === "platform_admin";

  // Chip del módulo activo: el ítem cuyo path es el prefijo más largo de la ruta.
  const active = CATALOG.filter((i) => i.path && location.pathname.startsWith(i.path)).sort(
    (a, b) => (b.path!.length - a.path!.length),
  )[0];

  function onLogout() {
    logout();
    navigate("/login");
  }

  return (
    <div className={cn("relative isolate flex h-svh flex-col overflow-hidden", theme.roleClass, launcherOpen && "launcher-open")}>
      <VisionEnvironment wallpaperKey={wallpaperKey} />

      <a
        href="#main-content"
        className="vision-focusable sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[200] focus:rounded-lg focus:bg-[var(--color-surface)] focus:px-4 focus:py-2"
      >
        Saltar al contenido
      </a>

      {/* TopBar cápsula */}
      <header
        className="vision-topbar sticky z-30 mx-3 flex h-14 items-center gap-3 rounded-full px-3"
        style={{ marginTop: "max(0.75rem, env(safe-area-inset-top))" }}
      >
        <button
          onClick={() => setLauncherOpen(true)}
          className="vision-focusable flex items-center gap-2 rounded-full px-2 py-1"
          aria-label="Abrir menú"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[var(--color-navy)] text-white">
            <Stethoscope size={16} />
          </span>
          <span className="hidden flex-col leading-none sm:flex">
            <span className="font-[var(--font-display)] text-[13px] font-bold text-[var(--color-text-primary)]">drnewenia</span>
            <span className="text-[9px] uppercase tracking-widest text-[var(--color-text-muted)]">Hospital virtual</span>
          </span>
        </button>

        {active && (
          <button
            onClick={() => setLauncherOpen(true)}
            className="vision-chip vision-focusable hidden items-center gap-2 rounded-full px-3 py-1 text-[12px] font-semibold sm:flex"
            style={{ ["--chip-color" as string]: active.color }}
            aria-label={`Módulo: ${active.label}. Abrir menú`}
          >
            <active.icon size={13} />
            {active.label}
          </button>
        )}

        {/* Orden alineado con el lenguaje de diseño (LEXCAR): grid(launcher) → tema → paleta → idioma → avatar. */}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setLauncherOpen(true)}
            className="vision-focusable flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-[var(--color-text-secondary)] transition hover:bg-white/10"
            aria-label="Abrir menú (⌘K)"
            title="Menú (⌘K)"
          >
            <LayoutGrid size={16} />
          </button>
          <ThemeToggle />
          <WallpaperPicker roleDefault={theme.wallpaper} />
          <LocaleSwitcher />
          <UserMenu
            name={user?.full_name}
            email={user?.email}
            roleLabel={theme.label}
            accent={theme.accent}
            accentTo={theme.accentTo}
            onLogout={onLogout}
          />
        </div>
      </header>

      {/* Ventana de contenido de cristal */}
      <main
        id="main-content"
        tabIndex={-1}
        className="vision-content-wrap relative z-10 flex-1 overflow-y-auto px-4 pb-24 pt-4 sm:px-8 lg:px-14"
      >
        {/* Contenedor plano: las páginas traen su propia animación de entrada (stagger).
            Animar también acá causaba que el wrapper quedara congelado a opacity < 1. */}
        <div className="mx-auto max-w-6xl p-6 lg:p-8">{pageReady && <Outlet />}</div>
      </main>

      <VisionDock pathname={location.pathname} />

      <VisionLauncher open={launcherOpen} onClose={() => setLauncherOpen(false)} isTeacher={isTeacher} isAdmin={isAdmin} />
    </div>
  );
}

// ── Menú de usuario (dropdown en la TopBar) ──────────────────────────────────
function UserMenu({
  name, email, roleLabel, accent, accentTo, onLogout,
}: {
  name: string | undefined; email: string | undefined; roleLabel: string; accent: string; accentTo: string; onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="vision-focusable flex h-9 items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] py-1 pl-1 pr-2.5 transition hover:bg-white/10"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menú de usuario"
      >
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold text-white"
          style={{ background: `linear-gradient(150deg, ${accent}, ${accentTo})` }}
        >
          {initials(name)}
        </span>
        <span className="hidden text-[12px] font-semibold text-[var(--color-text-primary)] sm:block">
          {name}
        </span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -6 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            role="menu"
            className="vision-modal-glass absolute right-0 top-12 w-60 overflow-hidden p-1.5"
          >
            <div className="border-b border-[var(--color-border)] px-3 py-2.5">
              <div className="truncate text-sm font-semibold text-[var(--color-text-primary)]">{name}</div>
              <div className="truncate text-xs text-[var(--color-text-secondary)]">{email}</div>
              <div className="mt-1 text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]">{roleLabel}</div>
            </div>
            <button
              onClick={onLogout}
              role="menuitem"
              className="mt-1 flex w-full items-center gap-2 rounded-[var(--radius-md)] px-3 py-2 text-sm text-[var(--color-text-secondary)] transition hover:bg-white/5 hover:text-[var(--color-text-primary)]"
            >
              <LogOut size={15} /> Cerrar sesión
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Dock inferior (macOS-style, solo desktop) ────────────────────────────────
const DOCK_LABELS = ["Biblioteca", "Mis pacientes", "Urgencia · Triage 3D", "Mapa cognitivo", "Flashcards", "Anatomía 3D"];
const DOCK_ITEMS: LauncherItem[] = DOCK_LABELS
  .map((l) => CATALOG.find((i) => i.label === l))
  .filter((i): i is LauncherItem => Boolean(i));

function DockIcon({ item }: { item: LauncherItem }) {
  return (
    <span
      className="vision-app-icon flex size-9 items-center justify-center transition-transform duration-200 group-hover:-translate-y-1.5 group-hover:scale-110"
      style={{ background: `linear-gradient(150deg, ${item.gradient[0]}, ${item.gradient[1]})` }}
    >
      <item.icon size={18} className="text-white" strokeWidth={2.2} />
    </span>
  );
}

function VisionDock({ pathname }: { pathname: string }) {
  return (
    <div
      className="vision-dock pointer-events-none fixed inset-x-0 z-40 hidden justify-center px-4 sm:flex"
      style={{ bottom: "max(1rem, env(safe-area-inset-bottom))" }}
    >
      <div className="vision-card-elevated pointer-events-auto flex items-center gap-1.5 rounded-[22px] px-2.5 py-2">
        {DOCK_ITEMS.map((item) => {
          const active = !!item.path && pathname.startsWith(item.path);
          if (item.action === "triage") {
            return (
              <button
                key={item.label}
                onClick={() => openTriageModule()}
                title={item.label}
                aria-label={item.label}
                className="vision-focusable group relative grid size-11 place-items-center rounded-[14px] transition active:scale-95"
              >
                <DockIcon item={item} />
              </button>
            );
          }
          return (
            <NavLink
              key={item.label}
              to={item.path!}
              title={item.label}
              aria-label={item.label}
              aria-current={active ? "page" : undefined}
              className="vision-focusable group relative grid size-11 place-items-center rounded-[14px] transition active:scale-95"
            >
              <DockIcon item={item} />
              {active && (
                <span
                  className="absolute -bottom-0.5 size-1 rounded-full"
                  style={{ background: item.color }}
                />
              )}
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}
